// #region angular imports
import { HttpClient, HttpParams } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    inject,
    signal,
    untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { faPrescriptionBottleAlt } from '@fortawesome/pro-solid-svg-icons';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs';
// #endregion

// #region app imports
import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { ShipmentStepEnum } from 'app/core/enums/generated/ShipmentStepEnum';
import { SecurityService } from 'app/core/security/security.service';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { UserJsVm } from 'app/shared/generated/Administration/Models/Users/UserJsVm';
import { RecentShipmentVm } from 'app/shared/generated/Inventory/Models/Shipping/RecentShipmentVm';
import { PatientDataVm } from 'app/shared/generated/Models/PatientDataVm';
import { SelectItem } from 'app/shared/models/select-item';
import { QuickUserInfoService } from 'app/shared/quick-user-info/quick-user-info.service';
import { SharedModule } from 'app/shared/shared.module';
import { ResizableWidgetComponent } from '../resizable-widget/resizable-widget.component';
import { LinkComponent } from 'app/shared/link/link.component';
import { TooltipModule } from 'primeng/tooltip';
// #endregion

@Component({
    selector: 'pcg-resizable-refill-request',
    imports: [
        FormsModule, 
        NgbModule, 
        SharedModule,
    
        LinkComponent,
        ResizableWidgetComponent,
        TooltipModule
    ],
    templateUrl: './resizable-refill-request.html',
    styleUrls: ['./resizable-refill-request.scss', '../resizable-widget/resizable-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResizableRefillRequest {
    // #region injections
    readonly infoService = inject(QuickUserInfoService);
    private readonly toastr = inject(NotificationService);
    private readonly sec = inject(SecurityService);
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly cdr = inject(ChangeDetectorRef);
    // #endregion

    // #region component signals
    readonly user = signal<UserJsVm | null>(null);
    readonly isUser = computed(() => this.user()?.isRxUser);

    rxNumberInput = signal<string | null>(null);
    // #endregion

    readonly pharmacyBaEnum = BusinessAreaEnum.Inventory;

    //#region Inventory Site Id Handling
    readonly inventoryId = signal<number | null>(null);
    readonly selectedInventoryId = signal<number | null>(null);
    readonly inventories = signal<SelectItem[]>([]);
    readonly multipleInventories = signal<boolean>(false);
    //#endregion

    //#region Facility Selection
    readonly facility = computed(() => this.infoService.userFacility());
    readonly isFacilityPharmacy = computed(() => this.facility()?.supportsRxRequests);
    //#endregion

    //#region Processing Signals
    readonly isProcessesing = computed(() =>         
        this.findingRx() 
        || this.creatingRefill() 
        || this.redirectingToRefill()
    );
    readonly disabledScan = computed(() => 
        this.isProcessesing()
        || !this.isFacilityPharmacy()  
        || !this.facility()
    );
    readonly fetching = signal<boolean>(false);
    readonly findingRx = signal<boolean>(false);
    readonly redirectingToRefill = signal<boolean>(false);
    readonly creatingRefill = signal<boolean>(false);
    readonly canAccessPharmacy = signal<boolean>(false);

    readonly buttonText = computed(() => {
        if (!this.facility()) return 'Select Facility To Create Request';
        if (!this.isFacilityPharmacy()) return 'Facility does not Support Requests';
        if (!this.disabledScan()) { return 'Create New Refill Request'; }
        if (this.findingRx()) { return 'Getting Rx Info...'; }
        if (this.redirectingToRefill()) { return 'Redirecting to Refill...'; }
        return 'Creating Refill...';
    });
    //#endregion

    //#region Refill list
    readonly recentRefills = signal<RecentShipmentVm[] | null>(null);
    readonly page = signal<number>(1);
    readonly outOfRefills = signal<boolean>(false);
    //#endregion

    readonly fa = { bottle: faPrescriptionBottleAlt };
    private readonly SCROLL_OFFSET = 100;

    /**
     * Subscribes to facility-change events so refill data
     * is refreshed whenever the user switches facilities.
     */
    readonly bindToFacilityChanges = effect(() => {
        this.infoService.facilityChangeNotifier();
        if (!this.infoService.isInitialized()) { return; }
        untracked(() => {
            this.resetList();
            if (this.infoService.shouldForceOpen()) {
                this.infoService.openModal();
            } else if (this.canAccessPharmacy() && this.infoService.userFacilityId()) {
                this.getShipments();
            }
        });
    });

    ngOnInit() { this.initialize(); }

    //#region Getter
    getShipments(): void {
        this.fetching.set(true);
        const params = new HttpParams().set('silent', true).set('page', this.page());

        this.http
            .get<SystemMessage>(`api/Inventory/RxFulfillmentUser/GetRecentRefillsByFacility/${this.infoService.userFacilityId()}`, { params })
            .subscribe({
                next: (r: SystemMessage) => {
                    this.recentRefills()
                        ? this.recentRefills.update((refills: RecentShipmentVm[]) =>
                            Array.from(new Set([...refills, r.model])).filter(
                                (refill: RecentShipmentVm) => refill?.shipmentId != null,
                            ),
                        )
                        : this.recentRefills.set(r.model);

                    this.fetching.set(false);
                    if (r?.value) { this.outOfRefills.set(true); }
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    console.error('Error fetching shipments:', error);
                    this.toastr.showError('Error fetching recent refills');
                    this.fetching.set(false);
                    this.cdr.markForCheck();
                },
            });
    }
    //#endregion

    //#region Create
    newRefill(): void {
        let rx = this.rxNumberInput();
        if (!rx || rx.trim() === '') {
            this.toastr.showError('Please enter a valid Rx number');
            return;
        }
        
        this.findRxInfo(rx).subscribe((rxInfo: PatientDataVm) => {
            this.findingRx.set(false);
            if (!this.canCreateRx(rxInfo)) { return; }

            this.createRefill(rxInfo)
                .pipe(finalize(() => this.creatingRefill.set(false)))
                .subscribe((refill: PatientDataVm) => {
                    if (refill?.errorMessage) {
                        this.toastr.showError(refill.errorMessage);
                        return;
                    }
                    this.toastr.showSuccess('Refill Request Created Successfully');
                    this.redirectingToRefill.set(true);
                    this.selectedInventoryId.set(null);
                    this.router.navigateByUrl(`/pharmacy/fulfillment/edit/${refill.shipmentId}/${this.inventoryId()}`);
                });
        });
    }

    findRxInfo(rx: string) {
        this.findingRx.set(true);
        const params = new HttpParams()
            .set('silent', true)
            .set('rxNumber', rx)
            .set('scanned', true)
            .set('isUser', true)
            .set('isFacility', true);
        return this.http.get<PatientDataVm>('api/Inventory/QS1/GetRxData', { params });
    }

    canCreateRx(data: PatientDataVm): boolean {
        if (data?.errorMessage) {
            this.toastr.showError(data.errorMessage);
            return false;
        }

        const maxStock = Math.max(...data.inventories.map((o) => o.currentStock));
        const invId = data.inventories.find((o) => o.currentStock == maxStock).inventorySiteId;
        this.inventoryId.set(invId);
        return true;
    }

    handleInventories(data: PatientDataVm): void {
        if ((data.inventories?.length ?? 0) > 1) {
            this.multipleInventories.set(true);
            const invsToList = data.inventories!.map((inv) => {
                const item = new SelectItem();
                item.text = inv.inventoryName ?? '';
                item.value = inv.inventorySiteId;
                return item;
            });
            this.inventories.set(invsToList);
        }
    }

    createRefill(body: PatientDataVm) {
        this.creatingRefill.set(true);
        // Widget is facility-user only; some GetRxData paths return the VM without
        // isFacility set, which would create the shipment as a Home delivery
        body.isFacility = true;
        const params = new HttpParams()
            .set('silent', true)
            .set('isUser', true)
            .set('inventorySiteId', this.inventoryId()!)
            .set('facilityId', this.infoService.userFacilityId());
        return this.http.post<PatientDataVm>('api/Inventory/QS1/ValidateAndSaveRxData', body, { params });
    }
    //#endregion

    //#region Helpers
    /**
     * ShipmentStepEnum text is viewed by internal (BPHP users)
     * BUT, external users see different text to mask steps
     */
    convertStepName(step: ShipmentStepEnum): string {
        switch (step) {
            case ShipmentStepEnum.StepNegativeOne: return 'Not Submitted';
            case ShipmentStepEnum.StepZero: return 'Submitted';
            case ShipmentStepEnum.StepOne: return 'Submitted';
            case ShipmentStepEnum.StepTwo: return 'In Progress';
            case ShipmentStepEnum.StepThree: return 'Fulfilled';
            case ShipmentStepEnum.StepFour: return 'Shipped';
            case ShipmentStepEnum.StepFive: return 'Delivered';
            default: return 'Step Not Found';
        }
    }

    redirect(row: RecentShipmentVm) {
        return `pharmacy/fulfillment/edit/${row.shipmentId}/${row.inventoryId}`;
    }

    onScroll(event: Event): void {
        const element = event.target as HTMLElement;
        if (
            element.scrollTop + element.clientHeight >= element.scrollHeight - this.SCROLL_OFFSET
            && !this.outOfRefills()
        ) {
            this.showMore();
        }
    }

    showMore(): void {
        this.page.set(this.page() + 1);
        if (this.canAccessPharmacy()) { this.getShipments(); }
    }

    resetList(): void {
        this.page.set(1);
        this.recentRefills.set(null);
        this.outOfRefills.set(false);
        this.selectedInventoryId.set(null);
    }
    //#endregion

    // #region Private helpers

    private initialize(): void {
        this.user.set(this.sec.getUser());
        this.canAccessPharmacy.set(
            this.sec.hasModuleAccess([BusinessAreaEnum.Inventory], SecurityService.setMinRole(PermissionRole.User)),
        );

        if (this.canAccessPharmacy() && this.infoService.userFacilityId()) {
            this.getShipments();
        }
    }
    // #endregion
}
